import { useRef, useState } from 'react'
import { Image, StyleSheet } from 'react-native'

import { MotiView } from '@zet/common/components/motiView'
import {
  B4,
  B5,
  B6,
  colors,
  CTN,
  H6,
  SH2,
  Stack,
  XStack,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import ArrowLeft from '@zet/design-system/svg/ArrowLeft'
import ArrowRight from '@zet/design-system/svg/ArrowRight'
import CheckBox from '@zet/design-system/svg/CheckBox'
import CheckBoxTickFilled from '@zet/design-system/svg/CheckBoxTickFilled'
import { DEVICE_WIDTH } from '@zet/utils/Constants'
import { LinearGradient } from 'expo-linear-gradient'
import { FlatList, ScrollView } from 'react-native-gesture-handler'

import { useApiAction } from '../../api/useApiAction'
import RewardPoint from '../../assets/images/icons/rewardPoint.svg'
import {
  IKeyBenefitItem,
  MoreBenefits,
  ProductSelectionProps,
  ProductSelectionSalientItem,
  ReadMoreState,
} from '../../types'
import { ICON_MAP } from '../../utils/iconMap'
import ActionButton from '../common/actionButtons/actionButton'
import { ConsentContent } from '../common/consentContent/index.web'

const MAX_PAGE_WIDTH = 640
const MAX_CARD_WIDTH = 360

const PAGE_WIDTH = Math.min(DEVICE_WIDTH, MAX_PAGE_WIDTH)
const CARD_WIDTH = Math.min(MAX_CARD_WIDTH, DEVICE_WIDTH * 0.6)
const SCROLL_CONTAINER_SPACING = (PAGE_WIDTH - CARD_WIDTH) / 2

export const ProductSelection = ({ props }: any) => {
  const { state, data = {} } = useApiAction()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [scrollEnabled, setScrollEnabled] = useState(false)
  const [readMoreStates, setReadMoreStates] = useState<ReadMoreState>({
    welcome_benefits: false,
    dining: false,
    travel: false,
    additional_benefits: [],
  })
  const [userConsents, setUserConsents] = useState(
    Array(props?.consents?.length).fill(true)
  )
  const { layout } = data as ProductSelectionProps

  const flatListRef = useRef<FlatList>(null)

  const onNextImage = () => {
    setScrollEnabled(true)
    setCurrentIndex((prevIndex) => {
      const upcomingIndex = prevIndex + 1
      if (upcomingIndex < layout?.products?.length) {
        flatListRef.current?.scrollToIndex({
          index: upcomingIndex,
          animated: true,
          viewOffset: SCROLL_CONTAINER_SPACING,
        })
        return upcomingIndex
      }

      return prevIndex
    })
    setScrollEnabled(false)
  }

  const onPreviousImage = () => {
    setScrollEnabled(true)
    setCurrentIndex((prevIndex) => {
      const upcomingIndex = prevIndex - 1
      if (upcomingIndex >= 0) {
        flatListRef.current?.scrollToIndex({
          index: upcomingIndex,
          animated: true,
          viewOffset: SCROLL_CONTAINER_SPACING,
        })
        return upcomingIndex
      }

      return prevIndex
    })
    setScrollEnabled(false)
  }

  const showLeftArrow = () => {
    return layout?.products?.length > 1 && currentIndex > 0
  }

  const showRightArrow = () => {
    return (
      layout?.products?.length > 1 &&
      currentIndex < layout?.products?.length - 1
    )
  }

  const toggleReadMoreState = (key: string, index = 0) => {
    if (key === 'additional_benefits') {
      if (readMoreStates.additional_benefits.includes(index)) {
        const temp = [...readMoreStates.additional_benefits]

        const removalIndex = temp.indexOf(index)

        temp.splice(removalIndex, 1)

        setReadMoreStates((prevState) => ({
          ...prevState,
          additional_benefits: temp,
        }))
      } else {
        readMoreStates.additional_benefits.push(index)

        setReadMoreStates((prevState) => ({
          ...prevState,
          additional_benefits: readMoreStates.additional_benefits,
        }))
      }
    } else {
      setReadMoreStates((prevState) => ({
        ...prevState,
        [key]: !prevState[key],
      }))
    }
  }

  const handleChange = (index: number, value: boolean): void => {
    setUserConsents((prevArray: boolean[]) => {
      const newArray = [...prevArray]
      newArray[index] = value
      return newArray
    })
  }

  return (
    <>
      <ScrollView nestedScrollEnabled>
        <Stack>
          <LinearGradient
            className="py-2"
            colors={['#15468B', '#000000', '#296ECF']}
            start={[0, 1.0]}
            end={[1, 0.0]}
          >
            <SH2 className="px-3 text-[#FACD75]">🎉 Congratulations</SH2>
            <H6 className="px-3 pb-3 text-white">{layout?.title}</H6>
            {layout?.products?.length > 1 ? (
              <>
                <FlatList
                  ref={flatListRef}
                  data={layout?.products}
                  horizontal
                  disableScrollViewPanResponder={true}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{
                    paddingHorizontal: SCROLL_CONTAINER_SPACING,
                    marginBottom: 16,
                  }}
                  scrollEnabled={scrollEnabled}
                  renderItem={({ item, index }) => {
                    return (
                      <MotiView
                        animate={{
                          scale: currentIndex === index ? 1 : 0.8,
                          opacity: currentIndex === index ? 1 : 0.8,
                        }}
                        transition={{
                          delay: 200,
                          duration: 300,
                          type: 'timing',
                        }}
                        style={{
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'center',
                          width: CARD_WIDTH,
                          aspectRatio: 1.6,
                        }}
                      >
                        <Image
                          key={index}
                          className="aspect-[1.6] w-full"
                          source={{
                            uri: item?.productImageUrl,
                          }}
                          resizeMode="contain"
                          resizeMethod="resize"
                        />
                      </MotiView>
                    )
                  }}
                />
                {showLeftArrow() && (
                  <Stack
                    className="absolute left-3  justify-center rounded-md"
                    style={styles.arrow}
                  >
                    <Pressable onPress={onPreviousImage}>
                      <ArrowLeft color="white" size={36} />
                    </Pressable>
                  </Stack>
                )}

                {showRightArrow() && (
                  <Stack
                    className="absolute right-3 justify-center rounded-md"
                    style={styles.arrow}
                  >
                    <Pressable onPress={onNextImage}>
                      <ArrowRight color="white" size={36} />
                    </Pressable>
                  </Stack>
                )}
                <Stack className="bg-tertiary-20 mb-4 self-center rounded-full px-3 py-1">
                  <CTN className="text-secondary-50">
                    {currentIndex + 1} / {layout?.products.length} card
                  </CTN>
                </Stack>
              </>
            ) : (
              <Image
                source={{
                  uri: layout?.products[0]?.productImageUrl || '',
                }}
                resizeMode="cover"
                className="bg-grays-50 mb-6 aspect-[1.6] w-full max-w-[360px] self-center rounded-lg"
              />
            )}
          </LinearGradient>
        </Stack>
        {/* BODY SECTION */}
        <Stack>
          {/* TITLE SECTION */}
          <Stack className="border-b-grays-30 border-b">
            <Stack className="absolute top-[-12px] w-full items-center rounded-t-md bg-white py-1">
              <Stack className="bg-grays-30 h-[4px] w-[52px] rounded" />
            </Stack>
            <Stack className="py-4/2 px-3">
              <H6>{layout?.products[currentIndex]?.title}</H6>
            </Stack>
          </Stack>
          {/* SALIENT FEATURES SECTION */}
          {layout?.products[currentIndex]?.salientFeatures?.length > 0 && (
            <XStack className="border-b-grays-30 flex-wrap gap-3 border-b-4 p-3">
              {layout?.products[currentIndex]?.salientFeatures?.map(
                (item: ProductSelectionSalientItem, index) => {
                  if (item?.title)
                    return (
                      <SalientFeatureItem
                        {...item}
                        key={index}
                        index={index}
                        widthClassName={
                          layout?.products[currentIndex]?.salientFeatures
                            .length %
                            2 ===
                            0 && index === 0
                            ? 'w-full'
                            : 'w-auto'
                        }
                      />
                    )
                  const Icon = ICON_MAP[item?.icon || 'OFFER_FILLED']
                  return (
                    <>
                      <XStack
                        className="w-full gap-1 p-2"
                        style={{ backgroundColor: '#FFF9EC' }}
                      >
                        <Icon />
                        <B5>{item?.description}</B5>
                      </XStack>
                      {/* <XStack className="items-center">
                        <DocumentOutlined size={18} />
                        <B4 className="ml-1 text-[#596379]">
                          <B4 className="font-[Graphie-Bold] underline">
                            {brandsI18n.t('hdfc.clickHere')}
                          </B4>
                          {brandsI18n.t('hdfc.viewFees')}
                        </B4>
                      </XStack> */}
                    </>
                  )
                }
              )}
            </XStack>
          )}
          {/* KEY BENEFITS SECTION */}
          {layout?.products[currentIndex]?.keyBenefits?.items?.length > 0 && (
            <Stack className="bg-primary-10 pt-4">
              <H6 className="text-primary-150 px-4">
                {layout?.products[currentIndex]?.keyBenefits?.title}
              </H6>
              <XStack className="border-b-grays-30 flex-wrap gap-3 border-b-4 p-3">
                {layout?.products[currentIndex]?.keyBenefits?.items?.map(
                  (item: IKeyBenefitItem, index) => (
                    <KeyBenefitItem {...item} key={index} />
                  )
                )}
              </XStack>
            </Stack>
          )}
          {/*Additional Info Section */}
          {layout?.products[currentIndex]?.additionalBenefits && (
            <Stack className="m-3 gap-y-3">
              <H6>
                {layout?.products[currentIndex]?.additionalBenefits?.title}
              </H6>
              {layout?.products[currentIndex]?.additionalBenefits?.types?.map(
                (benefits: MoreBenefits, benefitsIndex: number) => (
                  <Stack
                    className="border-grays-40 gap-y-1 rounded-lg border p-3"
                    key={benefitsIndex}
                  >
                    <Stack>
                      <H6 className="text-primary-200">{benefits?.title}</H6>
                      {benefits?.items?.map((item: string, index: number) => {
                        return readMoreStates['additional_benefits'].includes(
                          benefitsIndex
                        ) || index === 0 ? (
                          <B4
                            className="text-grays-60 flex-1 whitespace-normal"
                            key={index}
                            numberOfLines={
                              !readMoreStates['additional_benefits'].includes(
                                index
                              )
                                ? 2
                                : null
                            }
                          >
                            {index + 1}. {item}
                          </B4>
                        ) : (
                          <></>
                        )
                      })}
                      {benefits?.items?.length > 1 && (
                        <Pressable
                          onPress={() =>
                            toggleReadMoreState(
                              'additional_benefits',
                              benefitsIndex
                            )
                          }
                        >
                          <B4 className="underline">
                            {readMoreStates['additional_benefits'].includes(
                              benefitsIndex
                            )
                              ? 'Read Less'
                              : 'Read More'}
                          </B4>
                        </Pressable>
                      )}
                    </Stack>
                  </Stack>
                )
              )}
            </Stack>
          )}
          {/* MORE BENEFITS SECTION */}
          {/* {layout?.products[currentIndex]?.tnc && (
            <Stack className="m-3 gap-y-3">
              <H6> {brandsI18n.t('hdfc.moreBenefits')}</H6>
              <Stack className="border-grays-40 gap-y-1 rounded-lg border p-3">
                <Stack>
                  <H6 className="text-primary-200">
                    {layout?.products[currentIndex]?.tnc?.title}
                  </H6>
                  {layout?.products[currentIndex]?.tnc?.content?.map(
                    (item: string, index: number) => {
                      return readMoreStates['welcome_benefits'] ||
                        index === 0 ? (
                        <B4
                          className="text-grays-60 flex-1 whitespace-normal"
                          key={index}
                          numberOfLines={
                            !readMoreStates['welcome_benefits'] &&
                            index === 0 &&
                            2
                          }
                        >
                          {index + 1}. {item}
                        </B4>
                      ) : (
                        <></>
                      )
                    }
                  )}
                  <Pressable
                    onPress={() => toggleReadMoreState('welcome_benefits')}
                  >
                    <B4 className="underline">
                      {readMoreStates['welcome_benefits']
                        ? 'Read Less'
                        : 'Read More'}
                    </B4>
                  </Pressable>
                </Stack>
              </Stack>
            </Stack>
          )} */}
          {/* TNC SECTION */}
          <Stack className="m-3 gap-y-3">
            <H6>{layout?.products[currentIndex]?.tnc?.title}</H6>
            <Stack className="gap-y-1">
              {layout?.products[currentIndex]?.tnc?.content?.map(
                (item: string, index: number) => (
                  <B4
                    className="text-grays-70 flex-1 whitespace-normal"
                    key={index}
                  >
                    {index + 1}. {item}
                  </B4>
                )
              )}
            </Stack>
          </Stack>
        </Stack>
      </ScrollView>
      <Stack className="border-grays-30 border-t">
        {!!props?.consents &&
          userConsents &&
          props?.consents?.map((consent: string, index: number) => {
            return (
              <XStack className="px-3 py-2" key={index}>
                {userConsents[index] ? (
                  <Pressable onPress={() => handleChange(index, false)}>
                    <CheckBoxTickFilled color={colors.primary[100]} size={24} />
                  </Pressable>
                ) : (
                  <Pressable onPress={() => handleChange(index, true)}>
                    <CheckBox color={colors.primary[100]} size={24} />
                  </Pressable>
                )}
                <Stack className="ml-1 flex-1">
                  <ConsentContent
                    consentData={consent}
                    index={index}
                    showReadMoreButton={false}
                  />
                </Stack>
              </XStack>
            )
          })}
        <ActionButton
          title="Apply Now"
          disabled={!userConsents.every((element) => element === true)}
          action={'STATE_MACHINE_POST'}
          payload={{
            productId: layout?.products[currentIndex]?.id,
            productCode: layout?.products[currentIndex]?.productCode,
            productName: layout?.products[currentIndex]?.title,
          }}
        />
      </Stack>
    </>
  )
}

const SalientFeatureItem = ({
  title,
  description,
  subtext,
  widthClassName,
  icon,
}: ProductSelectionSalientItem & { widthClassName: string; index: number }) => {
  const Icon = ICON_MAP[icon || 'COIN_FILLED']

  return (
    <Stack
      className={`px-4/2 bg-grays-20 flex-grow gap-y-2 rounded py-2 ${widthClassName}`}
    >
      <XStack className="gap-1">
        <Icon />
        <B4>{title}</B4>
      </XStack>
      <XStack className="gap-x-1">
        <H6>{description}</H6>
        <B6 className="text-grays-80">{subtext}</B6>
      </XStack>
    </Stack>
  )
}

const KeyBenefitItem = ({ title, description, icon }: IKeyBenefitItem) => {
  return (
    <Stack className={`w-full`}>
      <XStack className="items-center gap-1">
        <RewardPoint />
        <B4 className="text-grays-80">{title}</B4>
      </XStack>
    </Stack>
  )
}

const styles = StyleSheet.create({
  arrow: {
    top: '45%',
  },
})
